import React, { useState, useEffect, useRef } from "react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid2';
import {
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LinkIcon from '@mui/icons-material/Link';
import axiosInstance from './axiosInstance';

import { useBreadcrumbs } from './BreadcrumbBar';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

// grafiken
import linkedinIcon from '../assets/linkedin.webp';
import instagramIcon from '../assets/instagram.webp';
import blogIcon from '../assets/webflow.webp';


// ── Baserow field keys ───────────────────────────────────────────────────────
const BASEROW_FIELDS = {
  TITLE:          'field_6416070',
  IMAGE1:         'field_6416085',
  IMAGE2:         'field_6416088',
  IMAGE3:         'field_6416089',
  POST_LINKEDIN:  'field_6416081',
  POST_INSTAGRAM: 'field_6416082',
  POST_BLOG:      'field_6416083',
  GENERATED_AT:   'field_6416086',
  SOURCE_URL:     'field_6416076',
  IMAGE_MODEL:    'field_9089356',
};
const IMAGE_FIELDS = [BASEROW_FIELDS.IMAGE1, BASEROW_FIELDS.IMAGE2, BASEROW_FIELDS.IMAGE3];

// ── Webhook URLs ─────────────────────────────────────────────────────────────
const API_START_JOB  = '/content-generation/start/';
const API_GET_RESULT = '/content-generation/result/';
//const API_START_JOB  = 'https://kinflow.app.n8n.cloud/webhook-test/start-job';
//const API_GET_RESULT = '';

const MAX_POLLS     = 120;
const POLL_INTERVAL = 5000;

// ── History pagination ──────────────────────────────────────────────────────
const HISTORY_PAGE_SIZE = 6;

// ── Channel config ───────────────────────────────────────────────────────────
const CHANNELS = [
  { id: 'linkedin',  label: 'LinkedIn',  icon: linkedinIcon },
  { id: 'blog',      label: 'Blog',      icon: blogIcon },
  { id: 'instagram', label: 'Instagram', icon: instagramIcon },
];

const PROGRESS_STEPS = [
  'Artikel / Thema wird analysiert …',
  'Social Media Posts werden erstellt …',
  'Bilder werden generiert …',
];

const STATUS_LABELS = {
  pending:    'Job wartet auf Verarbeitung …',
  processing: 'Job wird verarbeitet …',
  completed:  'Job abgeschlossen!',
  failed:     'Job fehlgeschlagen',
};

const CHANNEL_LABELS = { linkedin: 'LinkedIn', instagram: 'Instagram', blog: 'Blog' };

// ── Model config ───────────────────────────────────────────────────────────
const MODELS = [
  { id: 'gemini-2.5-flash-image', label: 'Nano Banana' },
  { id: 'gpt-image-2',            label: 'GPT Image 2'   },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ── Collapsible post text ────────────────────────────────────────────────────
function PostText({ text }) {
  const [expanded, setExpanded] = useState(false);
  const long = text && text.length > 400;

  return (
    <Box>
      <Box
        sx={(theme) => ({
          bgcolor: theme.palette.tokens.tableHead,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          whiteSpace: 'pre-wrap',
          lineHeight: 1.7,
          fontSize: 14,
          maxHeight: expanded || !long ? 'none' : 160,
          overflow: 'hidden',
          position: 'relative',
          ...(!expanded && long && {
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0, left: 0, right: 0,
              height: 60,
              background: `linear-gradient(to bottom, transparent, ${theme.palette.tokens.tableHead})`,
            },
          }),
        })}
      >
        {text}
      </Box>
      {long && (
        <Button
          size="small"
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setExpanded(v => !v)}
          sx={{ mt: 0.5 }}
        >
          {expanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
        </Button>
      )}
    </Box>
  );
}

// ── History post card ────────────────────────────────────────────────────────
function HistoryCard({ item, onImageClick }) {
  const availableChs = CHANNELS
    .map(c => c.id)
    .filter(id => {
      const fieldMap = {
        linkedin:  BASEROW_FIELDS.POST_LINKEDIN,
        instagram: BASEROW_FIELDS.POST_INSTAGRAM,
        blog:      BASEROW_FIELDS.POST_BLOG,
      };
      return item[fieldMap[id]] && item[fieldMap[id]].trim();
    });

  const [activeTab, setActiveTab] = useState(availableChs[0] || '');
  const [copied, setCopied]       = useState(false);

  const postFieldMap = {
    linkedin:  BASEROW_FIELDS.POST_LINKEDIN,
    instagram: BASEROW_FIELDS.POST_INSTAGRAM,
    blog:      BASEROW_FIELDS.POST_BLOG,
  };

  const currentText = activeTab ? (item[postFieldMap[activeTab]] || '') : '';

  const handleCopy = () => {
    if (!currentText) return;
    navigator.clipboard.writeText(currentText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const images = IMAGE_FIELDS.flatMap(f => item[f] || []);

  const generatedAt = new Date(item[BASEROW_FIELDS.GENERATED_AT])
    .toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
    .replace(',', '');

  const sourceUrl = item[BASEROW_FIELDS.SOURCE_URL];

  // ── Verwendetes Bildmodell ──────────────────────────────────────────────
  const imageModelId    = item[BASEROW_FIELDS.IMAGE_MODEL];
  const imageModelLabel = imageModelId
    ? (MODELS.find(m => m.id === imageModelId)?.label || imageModelId)
    : null;



  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <CardContent sx={{ pb: 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          noWrap
          title={item[BASEROW_FIELDS.TITLE]}
          sx={{ mb: 0.5 }}
        >
          {item[BASEROW_FIELDS.TITLE] || '–'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {generatedAt}
          </Typography>
          {sourceUrl && (
            <Chip
              icon={<LinkIcon sx={{ fontSize: '13px !important' }} />}
              label={sourceUrl.replace(/^https?:\/\//, '').slice(0, 30) + (sourceUrl.length > 35 ? '…' : '')}
              size="small"
              variant="outlined"
              component="a"
              href={sourceUrl}
              target="_blank"
              clickable
              sx={{ fontSize: 11, height: 20, cursor: 'pointer' }}
            />
          )}
          {imageModelLabel && (
            <Chip
              icon={<AutoAwesomeIcon sx={{ fontSize: '13px !important' }} />}
              label={imageModelLabel}
              size="small"
              variant="outlined"
              color="secondary"
              sx={{ fontSize: 11, height: 20 }}
            />
          )}
        </Box>
      </CardContent>

      {/* Image strip */}
      {images.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            px: 2,
            pb: 1.5,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {images.map((file, idx) => (
            <Box
              key={idx}
              component="img"
              src={file.thumbnails?.card_cover?.url || file.url}
              alt={file.visible_name}
              onClick={() => onImageClick(file.url)}
              sx={{
                width: 220,
                height: 120,
                objectFit: 'cover',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
                cursor: 'pointer',
                transition: 'transform 0.15s',
                '&:hover': { transform: 'scale(1.05)' },
              }}
            />
          ))}
        </Box>
      )}

      {/* Channel tabs */}
      {availableChs.length > 0 && (
        <Box sx={{ borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': { minHeight: 36, py: 0, fontSize: 12, textTransform: 'none' },
            }}
          >
            {availableChs.map(ch => (
              <Tab key={ch} label={CHANNEL_LABELS[ch]} value={ch} />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Copy Button */}
      <CardActions sx={{ px: 2, pt: 1, pb: 0, justifyContent: 'flex-end' }}>
        <Tooltip title={copied ? 'Kopiert!' : 'Text kopieren'}>
          <Button
            size="small"
            startIcon={<ContentCopyIcon fontSize="small" color={copied ? 'success' : 'inherit'} />}
            onClick={handleCopy}
            disabled={!currentText}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            {copied ? 'Kopiert' : ''}
          </Button>
        </Tooltip>
      </CardActions>

      {/* Post text */}
      <CardContent sx={{ flex: 1, pt: 1.5, pb: '8px !important' }}>
        {currentText ? (
          <PostText text={currentText} />
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Kein Text für diesen Kanal.
          </Typography>
        )}
      </CardContent>

    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SocialMediaGenerator() {
  // input
  const [inputMode,      setInputMode]      = useState('url');
  const [urlValue,       setUrlValue]       = useState('');
  const [topicValue,     setTopicValue]     = useState('');
  const [channels,       setChannels]       = useState({ linkedin: false, blog: false, instagram: false });
  const [generateImages, setGenerateImages] = useState(true);
  const [imageModel,     setImageModel]     = useState(MODELS[0].id);

  // job / polling
  const [loading,   setLoading]   = useState(false);
  const [jobId,     setJobId]     = useState(null);
  const [jobStatus, setJobStatus] = useState('');
  const [progress,  setProgress]  = useState(0);
  const [elapsed,   setElapsed]   = useState(0);

  // results
  const [genResult, setGenResult] = useState(null);
  const [genError,  setGenError]  = useState('');
  const [copiedCh,  setCopiedCh]  = useState('');

  // resume dialog
  const [resumeDialog, setResumeDialog] = useState(null);

  // baserow
  const [baserowData,   setBaserowData]   = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [historyVisibleCount, setHistoryVisibleCount] = useState(HISTORY_PAGE_SIZE);

  // refs
  const pollingRef   = useRef(null);
  const timerRef     = useRef(null);
  const startTimeRef = useRef(null);
  const pollCountRef = useRef(0);

  // filter
  const [historyFilter, setHistoryFilter] = useState({ channel: 'all', search: '' });
  const [historyLimit, setHistoryLimit] = useState(6);

  // Breadcrumbs
  useBreadcrumbs(
       [
          { label: 'Marketing', path: '/marketing' },
          { label: 'Social Media Post Generator' },
        ]
  );

  // ── Baserow fetch ──────────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get('/content-generation/')
      .then(res => setBaserowData([...res.data].reverse()))
      .catch(err => console.error('API ERROR:', err));
  }, []);

  // ── Resume dialog on mount ─────────────────────────────────────────────────
  useEffect(() => {
    const savedId    = localStorage.getItem('currentJobId');
    const savedStart = localStorage.getItem('jobStartTime');
    if (savedId && savedStart) {
      const age = Date.now() - parseInt(savedStart, 10);
      if (age < 600_000) {
        setResumeDialog({ jobId: savedId, startTime: parseInt(savedStart, 10) });
      } else {
        localStorage.removeItem('currentJobId');
        localStorage.removeItem('jobStartTime');
      }
    }
  }, []);

  // ── Cleanup ────────────────────────────────────────────────────────────────
  useEffect(() => () => {
    clearInterval(pollingRef.current);
    clearInterval(timerRef.current);
  }, []);

  // ── Timer ──────────────────────────────────────────────────────────────────
  function startTimer(fromTime) {
    startTimeRef.current = fromTime;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  }

  function stopTimer() {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }

  // ── Polling ────────────────────────────────────────────────────────────────
  function startPolling(jId) {
    pollCountRef.current = 0;
    clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      pollCountRef.current += 1;

      if (pollCountRef.current > MAX_POLLS) {
        clearInterval(pollingRef.current);
        stopTimer();
        setGenError('Job dauert zu lange. Bitte lade die Seite neu und versuche es erneut.');
        setLoading(false);
        return;
      }

      try {
        const { data } = await axiosInstance.get(API_GET_RESULT, { params: { jobId: jId } });
        setJobStatus(data.status);
        setProgress(data.progress || 0);
        if (data.status === 'completed') {
          clearInterval(pollingRef.current);
          stopTimer();
          localStorage.removeItem('currentJobId');
          localStorage.removeItem('jobStartTime');
          setLoading(false);
          setGenResult(data.result);
        } else if (data.status === 'failed') {
          clearInterval(pollingRef.current);
          stopTimer();
          localStorage.removeItem('currentJobId');
          localStorage.removeItem('jobStartTime');
          setLoading(false);
          setGenError('Job fehlgeschlagen: ' + (data.error || 'Unbekannter Fehler'));
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, POLL_INTERVAL);
  }

  // ── Generate ───────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (inputMode === 'url') {
      if (!urlValue.trim()) { setGenError('Bitte gib eine gültige URL ein.'); return; }
      if (!urlValue.startsWith('http://') && !urlValue.startsWith('https://')) {
        setGenError('Die URL muss mit http:// oder https:// beginnen.'); return;
      }
    } else {
      if (!topicValue.trim()) { setGenError('Bitte gib ein Thema ein.'); return; }
    }
    if (!channels.linkedin && !channels.blog && !channels.instagram) {
      setGenError('Bitte wähle mindestens einen Kanal aus.'); return;
    }

    setGenError('');
    setGenResult(null);
    setLoading(true);
    setJobStatus('');
    setProgress(0);
    setElapsed(0);

    const payload = {
      inputType: inputMode,
      ...channels,
      generateImages,
      ...(generateImages ? { imageModel } : {}),
      ...(inputMode === 'url' ? { url: urlValue.trim() } : { topic: topicValue.trim() }),
    };

    try {
      const { data } = await axiosInstance.post(API_START_JOB, payload);
      console.log(payload);
      const newJobId = data.jobId;
      setJobId(newJobId);
      setJobStatus('pending');
      const now = Date.now();
      localStorage.setItem('currentJobId', newJobId);
      localStorage.setItem('jobStartTime', String(now));
      startTimer(now);
      startPolling(newJobId);
    } catch (err) {
      setGenError('Fehler beim Starten des Jobs. Bitte versuche es erneut.');
      setLoading(false);
      stopTimer();
    }
  };

  // ── Resume ─────────────────────────────────────────────────────────────────
  const handleResumeConfirm = () => {
    const { jobId: jId, startTime } = resumeDialog;
    setResumeDialog(null);
    setJobId(jId);
    setJobStatus('pending');
    setLoading(true);
    startTimer(startTime);
    startPolling(jId);
  };

  const handleResumeCancel = () => {
    localStorage.removeItem('currentJobId');
    localStorage.removeItem('jobStartTime');
    setResumeDialog(null);
  };

  // ── Copy (generator results) ───────────────────────────────────────────────
  const copyText = (ch, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedCh(ch);
      setTimeout(() => setCopiedCh(''), 2000);
    });
  };

  const stepIndex = Math.min(
    Math.floor((progress / 100) * PROGRESS_STEPS.length),
    PROGRESS_STEPS.length - 1,
  );

const filteredHistory = baserowData.filter(item => {
  if (historyFilter.channel !== 'all') {
    const fieldMap = {
      linkedin:  BASEROW_FIELDS.POST_LINKEDIN,
      instagram: BASEROW_FIELDS.POST_INSTAGRAM,
      blog:      BASEROW_FIELDS.POST_BLOG,
    };
    const field = fieldMap[historyFilter.channel];
    if (!item[field] || !item[field].trim()) return false;
  }
  if (historyFilter.search.trim()) {
    const q = historyFilter.search.toLowerCase();
    const title = (item[BASEROW_FIELDS.TITLE] || '').toLowerCase();
    const url   = (item[BASEROW_FIELDS.SOURCE_URL] || '').toLowerCase();
    if (!title.includes(q) && !url.includes(q)) return false;
  }
  return true;
});

const limitedHistory = historyLimit === 0
  ? filteredHistory
  : filteredHistory.slice(0, historyLimit);

const visibleHistory = limitedHistory.slice(0, historyVisibleCount);
const hasMoreHistory = historyVisibleCount < limitedHistory.length;
  

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Grid container spacing={3}>

      {/* Resume dialog */}
      <Dialog open={!!resumeDialog} onClose={handleResumeCancel}>
        <DialogTitle>Laufender Job gefunden</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Es läuft noch ein Job (ID: <strong>{resumeDialog?.jobId}</strong>).
            Möchtest du den Status abrufen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResumeCancel}>Abbrechen</Button>
          <Button variant="contained" onClick={handleResumeConfirm}>Fortfahren</Button>
        </DialogActions>
      </Dialog>

      {/* Page title */}
      <Grid size={12}>
        <Typography variant="h4" component="h2">Social Media Post Generator</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Artikel-URL oder Thema eingeben und Posts für alle Kanäle erstellen
        </Typography>
      </Grid>

      {/* ══ GENERATOR CARD ══════════════════════════════════════════════════ */}
      <Grid size={12}>
        <Card variant="outlined" >
          <CardContent>

            {/* Mode tabs */}
            <Tabs
              value={inputMode}
              onChange={(_, v) => v && setInputMode(v)}
              sx={{
                mb: 2.5,
                minHeight: 36,
                '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 2, textTransform: 'none' },
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                width: 'fit-content',
              }}
            >
              <Tab label="Artikel-URL" value="url" />
              <Tab label="Thema"       value="topic" />
            </Tabs>

            {/* URL input */}
            {inputMode === 'url' && (
              <TextField
                label="Artikel-URL"
                placeholder="https://example.com/artikel"
                value={urlValue}
                onChange={e => setUrlValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                fullWidth
                size="small"
                type="url"
                sx={{ mb: 2.5 }}
              />
            )}

            {/* Topic input */}
            {inputMode === 'topic' && (
              <TextField
                label="Thema"
                placeholder="Beschreibe das Thema, z. B. »Die neuesten Trends im KI-Marketing«"
                value={topicValue}
                onChange={e => setTopicValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && e.ctrlKey && handleGenerate()}
                fullWidth
                size="small"
                multiline
                minRows={3}
                helperText="Ctrl + Enter zum Absenden"
                sx={{ mb: 2.5 }}
              />
            )}

            {/* Channels + images in one row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 7 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Kanäle
                </Typography>
                <ToggleButtonGroup
                  value={Object.keys(channels).filter(k => channels[k])}
                  onChange={(_, newSelection) => {
                    setChannels({ linkedin: false, blog: false, instagram: false, ...Object.fromEntries(newSelection.map(k => [k, true])) });
                  }}
                  size="small"
                  sx={{ gap: 0.75, flexWrap: 'wrap', '& .MuiToggleButton-root': { borderRadius: '999px !important', border: '1px solid !important', px: 2, textTransform: 'none', fontSize: 13 } }}
                >
                  {CHANNELS.map(({ id, label, icon }) => (
                    <ToggleButton
                      key={id}
                      value={id}
                      sx={{ gap: 1 }}
                    >
                      <img
                        src={icon}
                        alt={label}
                        width={18}
                        height={18}
                      />
                      {label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>

              <Grid size={{ xs: 12, sm: 5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Bildgenerator
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={generateImages}
                      onChange={e => setGenerateImages(e.target.checked)}
                      size="small"
                    />
                  }
                  label="3 Bilder generieren"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 }, mb: generateImages ? 0.75 : 0, display: 'block' }}
                />
                {generateImages && (
                    <Grid>
                      <Tabs
                        value={imageModel}
                        onChange={(_, v) => v && setImageModel(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          display: "inline-flex",
                          width: "fit-content",
                          minHeight: 36,

                          "& .MuiTab-root": {
                            minHeight: 36,
                            py: 0.5,
                            px: 2,
                          },

                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        {MODELS.map(({ id, label }) => (
                          <Tab key={id} value={id} label={label} />
                        ))}
                      </Tabs>
                    </Grid>
                )}
              </Grid>
            </Grid>

            {/* Error */}
            {genError && (
              <Alert severity="error" onClose={() => setGenError('')} sx={{ mb: 2 }}>
                {genError}
              </Alert>
            )}

            {/* Submit */}
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            >
              {loading ? 'Wird verarbeitet …' : 'Post generieren'}
            </Button>
          </CardContent>

          {/* ── Progress panel ── */}
          {loading && (
            <CardContent sx={(theme) => ({ borderTop: '1px solid', borderColor: 'divider', bgcolor: theme.palette.tokens.tableHead, pt: 2 })}>
              <Grid container spacing={2}>

                <Grid size={{ xs: 8 }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>
                    {STATUS_LABELS[jobStatus] || 'Job wird gestartet …'}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 4 }} sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Zeit</Typography>
                  <Typography variant="body1" fontFamily="monospace" fontWeight={600} color="primary" sx={{ mt: 0.25 }}>
                    {formatTime(elapsed)}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Box sx={{ position: 'relative' }}>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{ height: 8, borderRadius: 999 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontWeight: 700, fontSize: 10,
                        color: 'text.primary', lineHeight: 1,
                      }}
                    >
                      {Math.round(progress)}%
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={12}>
                  <Stack spacing={0.75}>
                    {PROGRESS_STEPS.map((label, i) => {
                      const done    = i < stepIndex;
                      const current = i === stepIndex;
                      return (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: done || current ? 1 : 0.35 }}>
                          {done
                            ? <CheckCircleOutlineIcon fontSize="small" color="success" />
                            : current
                              ? <CircularProgress size={16} />
                              : <Box sx={(theme) => ({ width: 20, height: 20, borderRadius: '50%', border: '1.5px solid', borderColor: theme.palette.tokens.chartNeutral })} />
                          }
                          <Typography variant="body2" fontWeight={current ? 600 : 400}>{label}</Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Grid>

                {jobId && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">
                      Job-ID: <code>{jobId}</code>
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          )}
        </Card>
      </Grid>

      {/* ══ GENERATOR RESULTS ═══════════════════════════════════════════════ */}
      {genResult && (
        <>
          {Object.entries(genResult.channels || {}).map(([ch, chData]) => {
            const text = chData.postText || chData.text || '';
            if (!text.trim()) return null;
            return (
              <Grid size={{ xs: 12, md: 6 }} key={ch}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Chip
                        label={ch.charAt(0).toUpperCase() + ch.slice(1)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Tooltip title={copiedCh === ch ? 'Kopiert!' : 'Text kopieren'}>
                        <IconButton size="small" onClick={() => copyText(ch, text)}>
                          <ContentCopyIcon fontSize="small" color={copiedCh === ch ? 'success' : 'inherit'} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <PostText text={text} />
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {generateImages && (
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Generierte Bilder
              </Typography>
              {(genResult.images || []).filter(u => u && u.trim() && u.trim().toLowerCase() !== 'undefined').length === 0
                ? <Typography variant="body2" color="text.secondary">Es wurden keine Bilder generiert.</Typography>
                : (
                  <Grid container spacing={2}>
                    {(genResult.images || [])
                      .filter(u => u && u.trim() && u.trim().toLowerCase() !== 'undefined')
                      .map((imgUrl, i) => (
                        <Grid size={{ xs: 12, sm: 4 }} key={i}>
                          <Card variant="outlined">
                            <CardMedia
                              component="img"
                              image={imgUrl}
                              alt={`Generiertes Bild ${i + 1}`}
                              sx={{ height: 200, objectFit: 'cover', cursor: 'pointer' }}
                              onClick={() => setSelectedImage(imgUrl)}
                            />
                            <CardActions sx={{ justifyContent: 'center' }}>
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                component="a"
                                href={imgUrl}
                                download={`social-image-${i + 1}.jpg`}
                                target="_blank"
                              >
                                Download
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                )
              }
            </Grid>
          )}
        </>
      )}

      {/* Filter für gespeicherte Ergebnisse */}
      {baserowData.length > 0 && (
        <Grid size={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Gespeicherte Ergebnisse
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {baserowData.length} Posts gesamt
              </Typography>
            </Box>

            <Stack spacing={1.5}>

              {/* Zeile 1: Kanal + Suche */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>

                <ToggleButtonGroup
                  value={historyFilter.channel}
                  exclusive
                  onChange={(_, v) => {
                    if (v !== null) {
                      setHistoryFilter(f => ({ ...f, channel: v }));
                      setHistoryVisibleCount(HISTORY_PAGE_SIZE);
                    }
                  }}
                  size="small"
                  sx={{
                    gap: 0.5,
                    flexWrap: 'wrap',
                    '& .MuiToggleButton-root': {
                      borderRadius: '999px !important',
                      border: '1px solid !important',
                      px: 1.5,
                      textTransform: 'none',
                      fontSize: 12,
                    },
                  }}
                >
                  <ToggleButton value="all">Alle</ToggleButton>
                  {CHANNELS.map(({ id, label, icon }) => (
                    <ToggleButton key={id} value={id} sx={{ gap: 0.75 }}>
                      <img src={icon} alt={label} width={14} height={14} />
                      {label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <TextField
                  size="small"
                  placeholder="Titel oder URL suchen …"
                  value={historyFilter.search}
                  onChange={e => {
                    setHistoryFilter(f => ({ ...f, search: e.target.value }));
                    setHistoryVisibleCount(HISTORY_PAGE_SIZE);
                  }}
                  sx={{ minWidth: 220 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Box component="span" sx={{ mr: 0.5, color: 'text.secondary', display: 'flex' }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                      ...(historyFilter.search && {
                        endAdornment: (
                          <IconButton size="small" onClick={() => setHistoryFilter(f => ({ ...f, search: '' }))}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        ),
                      }),
                    },
                  }}
                />
              </Stack>

              {/* Zeile 2: Anzahl-Filter + Trefferzähler */}
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Anzeigen:
                </Typography>
                <ToggleButtonGroup
                  value={historyLimit}
                  exclusive
                  onChange={(_, v) => {
                    if (v !== null) {
                      setHistoryLimit(v);
                      setHistoryVisibleCount(HISTORY_PAGE_SIZE);
                    }
                  }}
                  size="small"
                  sx={{
                    gap: 0.5,
                    '& .MuiToggleButton-root': {
                      borderRadius: '999px !important',
                      border: '1px solid !important',
                      px: 1.5,
                      textTransform: 'none',
                      fontSize: 12,
                      minWidth: 40,
                    },
                  }}
                >
                  {[6, 12, 24, 50, 0].map(n => (
                    <ToggleButton key={n} value={n}>
                      {n === 0 ? 'Alle' : n}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {(historyFilter.channel !== 'all' || historyFilter.search.trim()) && (
                  <Typography variant="caption" color="text.secondary">
                    {filteredHistory.length} Treffer
                  </Typography>
                )}
              </Stack>

            </Stack>
          </Paper>
        </Grid>
      )}

      {/* ══ HISTORY GRID ════════════════════════════════════════════════════ */}
      {visibleHistory.map(item => (
        <Grid size={{ xs: 12, md: 6 }} key={item.id}>
          <HistoryCard item={item} onImageClick={setSelectedImage} />
        </Grid>
      ))}

      {/* ══ HISTORY "WEITER" BUTTON ═════════════════════════════════════════ */}
      {hasMoreHistory && (
        <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setHistoryVisibleCount(c => c + HISTORY_PAGE_SIZE)}
          >
            Mehr anzeigen
          </Button>
        </Grid>
      )}

      {/* ══ IMAGE LIGHTBOX ══════════════════════════════════════════════════ */}
      <Dialog
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent sx={{ p: 0, bgcolor: 'black', lineHeight: 0 }}>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Vorschau"
              sx={{ width: '100%', height: 'auto', display: 'block', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>

    </Grid>
  );
}